// Pure scoring for the data-driven quiz engine. Two modes:
//
//  • "tally" — each chosen option carries a `scores` map ({typeKey: points}).
//    We sum points per type across all answers; the highest total is the result.
//    Ties resolve to whichever type appears first in the quiz's `results` object
//    (stable, deterministic). Used by "which type are you" quizzes.
//
//  • "band" — each chosen option carries a numeric `value`. We sum the values and
//    map the total to the first band whose `max` it does not exceed. `bands` must
//    be listed ascending by `max`. Used by severity self-checks (stress, immune).
//
// `answers` is an array parallel to `def.questions`, each entry the selected
// option's index (or null if unanswered).

const chosenOptions = (def, answers) =>
  answers
    .map((optIndex, qIndex) => {
      const q = def.questions[qIndex];
      if (!q || optIndex == null) return null;
      return q.options[optIndex] || null;
    })
    .filter(Boolean);

const scoreTally = (def, answers) => {
  const resultKeys = Object.keys(def.results);
  const totals = Object.fromEntries(resultKeys.map((k) => [k, 0]));

  chosenOptions(def, answers).forEach((opt) => {
    Object.entries(opt.scores || {}).forEach(([key, pts]) => {
      if (key in totals) totals[key] += pts;
    });
  });

  const total = Object.values(totals).reduce((a, b) => a + b, 0) || 1;
  // Rank by points; ties break by declaration order in `results`.
  const ranked = resultKeys
    .map((key) => ({
      key,
      points: totals[key],
      percentage: Math.round((totals[key] / total) * 100),
    }))
    .sort((a, b) => b.points - a.points || resultKeys.indexOf(a.key) - resultKeys.indexOf(b.key));

  return { key: ranked[0].key, ranked, totals };
};

const scoreBand = (def, answers) => {
  const sum = chosenOptions(def, answers).reduce((acc, opt) => acc + (opt.value || 0), 0);
  const bands = [...def.scoring.bands].sort((a, b) => a.max - b.max);
  const band = bands.find((b) => sum <= b.max) || bands[bands.length - 1];
  return { key: band.key, score: sum, max: def.scoring.max };
};

// Returns { key, ... } — `key` is always the result key to route to.
export const scoreQuiz = (def, answers) =>
  def.scoring.mode === "band" ? scoreBand(def, answers) : scoreTally(def, answers);

export default scoreQuiz;
